/* eslint-disable camelcase */
import React, { useState, useEffect, useContext, useImperativeHandle, forwardRef, Suspense } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@material-ui/core';
import {
  MuiPickersUtilsProvider,
  KeyboardDateTimePicker,
} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import EditTwoToneIcon from '@material-ui/icons/EditTwoTone';
import styled from 'styled-components';
import 'react-map-gl-geocoder/dist/mapbox-gl-geocoder.css';
import { CircularProgressContext } from 'components/CircularProgressOverlay';
import { CallContext } from 'context/callContextContainer';
import { JobContext } from 'context/jobContextContainer';
import { vehiclesService } from 'services/vehiclesService';
import { notNull } from 'utils/text';

const moment = require('moment-timezone');

const SearchBox = React.lazy(() =>
  import('@mapbox/search-js-react').then(module => ({
    default: module.SearchBox,
  }))
);

const TableInfoContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  font-size: 13px;
  font-weight: 500;
  color: #666;
`;

const tableCellPadding = '4px 6px';

const StyledTableContainer = styled(TableContainer)`
  max-height: 500px;
  overflow: auto;
  & .MuiTableCell-root {
    padding: ${tableCellPadding};
  }
`;

const StyledTableHeadCell = styled(TableCell)`
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  padding: ${tableCellPadding} !important;
`;

const StyledTableCell = styled(TableCell)`
  font-size: 12px;
  white-space: nowrap;
  padding: ${tableCellPadding} !important;
`;

const StyledTableCellWrap = styled(TableCell)`
  font-size: 12px;
  white-space: normal;
  padding: ${tableCellPadding} !important;
`;

const StyledDialogContent = styled(DialogContent)`
  .MuiInputLabel-outlined {
    font-size: 14px;
    transform: translate(14px, 12px) scale(1);
    background: transparent;
    padding: 0 3px;

    &.MuiInputLabel-shrink {
      transform: translate(14px, -5px) scale(0.75);
    }
  }

  .MuiOutlinedInput-input {
    padding: 10px;
    font-size: 14px;
    height: auto;
  }
`;

const emptyScheduleLocation = () => ({
  place_name: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  lat: null,
  lng: null,
});

const parseVehicleAddress = address => {
  if (!address) {
    return null;
  }
  if (typeof address === 'object') {
    return address;
  }
  try {
    return JSON.parse(address);
  } catch (e) {
    return null;
  }
};

const getEffectiveVehicleAddress = (vehicle, setting) => {
  const parsed = parseVehicleAddress(vehicle?.address);
  if (parsed?.addr1) {
    return parsed;
  }

  if (!setting?.addr1) {
    return null;
  }

  return {
    place_name: '',
    addr1: setting.addr1 || '',
    addr2: setting.addr2 || '',
    city: setting.city || '',
    zip: setting.zip || '',
    state: setting.state || '',
    lat: parseInt(setting.lat, 10) || 0,
    lon: parseInt(setting.lon, 10) || 0,
  };
};

const vehicleAddressToScheduleLocation = vehicleAddress => {
  if (!vehicleAddress?.addr1) {
    return emptyScheduleLocation();
  }

  let address = vehicleAddress.addr1;
  if (address.includes(',') && (vehicleAddress.city || vehicleAddress.state || vehicleAddress.zip)) {
    address = address.split(',')[0].trim();
  }

  return {
    place_name: vehicleAddress.place_name || '',
    address,
    city: vehicleAddress.city || '',
    state: vehicleAddress.state || '',
    zip: vehicleAddress.zip || '',
    lat: vehicleAddress.lat ? parseFloat(vehicleAddress.lat) / 1000000 : null,
    lng: vehicleAddress.lon ? parseFloat(vehicleAddress.lon) / 1000000 : null,
  };
};

const getDefaultScheduleTimes = setting => {
  const timezone = setting?.timezone || 'America/New_York';
  const today = moment.tz(timezone).format('YYYY-MM-DD');

  return {
    startTimeLocal: moment.tz(`${today} 00:01:00`, 'YYYY-MM-DD HH:mm:ss', timezone).toDate(),
    endTimeLocal: moment.tz(`${today} 23:59:00`, 'YYYY-MM-DD HH:mm:ss', timezone).toDate(),
    startTimeUTC: moment.tz(`${today} 00:01:00`, 'YYYY-MM-DD HH:mm:ss', timezone).utc().format('YYYY-MM-DD HH:mm:ss'),
    endTimeUTC: moment.tz(`${today} 23:59:00`, 'YYYY-MM-DD HH:mm:ss', timezone).utc().format('YYYY-MM-DD HH:mm:ss'),
  };
};

const buildDefaultSchedule = (vehicle, setting) => {
  const location = vehicleAddressToScheduleLocation(getEffectiveVehicleAddress(vehicle, setting));
  const { startTimeUTC, endTimeUTC } = getDefaultScheduleTimes(setting);

  return {
    startLocation: location,
    endLocation: location,
    startTime: startTimeUTC,
    endTime: endTimeUTC,
  };
};

const scheduleToFormState = (schedule, setting) => {
  if (!schedule) {
    return null;
  }

  const timezone = setting?.timezone || 'America/New_York';
  const { startTimeLocal, endTimeLocal } = getDefaultScheduleTimes(setting);

  return {
    startLocation: schedule.startLocation || emptyScheduleLocation(),
    endLocation: schedule.endLocation || emptyScheduleLocation(),
    startTime: schedule.startTime ? moment.utc(schedule.startTime).tz(timezone).toDate() : startTimeLocal,
    endTime: schedule.endTime ? moment.utc(schedule.endTime).tz(timezone).toDate() : endTimeLocal,
  };
};

const VehicleStep = forwardRef(({ isActive }, ref) => {
  const { showCircularProgress, hideCircularProgress } = useContext(CircularProgressContext);
  const { siteSetting } = useContext(CallContext);
  const { routePlanData, setRoutePlanData } = useContext(JobContext);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [selected, setSelected] = useState([]);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleVehicleIds, setScheduleVehicleIds] = useState([]);
  const [startLocationSearch, setStartLocationSearch] = useState('');
  const [endLocationSearch, setEndLocationSearch] = useState('');
  const [showUnscheduledDialog, setShowUnscheduledDialog] = useState(false);
  const [unscheduledVehicleIds, setUnscheduledVehicleIds] = useState([]);
  const [alertMessage, setAlertMessage] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    startLocation: {
      place_name: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      lat: null,
      lng: null,
    },
    endLocation: {
      place_name: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      lat: null,
      lng: null,
    },
    startTime: null,
    endTime: null,
  });

  useEffect(() => {
    // Only fetch when step is active and we haven't fetched yet
    if (isActive && !hasFetched) {
      const fetchVehicles = async () => {
        setLoading(true);
        showCircularProgress('Loading vehicles...');

        try {
          const response = await vehiclesService.get_all_vehicles();
          if (response?.data?.status === 'SUCCESS' && response?.data?.data) {
            const vehiclesData = Array.isArray(response.data.data) ? response.data.data : [];
            const setting = siteSetting();
            const vehiclesWithSchedule = vehiclesData.map(vehicle => ({
              ...vehicle,
              schedule: buildDefaultSchedule(vehicle, setting),
            }));
            setVehicles(vehiclesWithSchedule);
            const allVehicleIds = vehiclesWithSchedule.map(vehicle => vehicle.id);
            setSelected(allVehicleIds);
          } else {
            setVehicles([]);
            setSelected([]);
          }
        } catch (error) {
          console.error('Error fetching vehicles:', error);
          setVehicles([]);
          setSelected([]);
        } finally {
          setLoading(false);
          hideCircularProgress();
          setHasFetched(true);
        }
      };

      fetchVehicles();
    }
  }, [isActive, hasFetched]);

  const getStatusText = (status) => {
    return status == 1 ? 'Active' : 'Inactive';
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const allVehicleIds = vehicles.map(vehicle => vehicle.id);
      setSelected(allVehicleIds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, vehicleId) => {
    const selectedIndex = selected.indexOf(vehicleId);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, vehicleId);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const onGeocodeResult = (type, result) => {
    if (result?.features?.length === 0) {
      return;
    }

    const { geometry, properties } = result.features[0];

    let placeName = '';
    let address = '';
    let city = '';
    let state = '';
    let zip = '';

    if (properties.context?.postcode) {
      zip = properties.context.postcode.name;
    }
    if (properties.context?.region) {
      state = properties.context.region.name;
    }
    if (properties.context?.place) {
      city = properties.context.place.name;
    } else if (properties.context?.locality) {
      city = properties.context.locality.name;
    }
    if (properties.context?.address) {
      address = properties.context.address.name;
    } else if (properties.address) {
      address = properties.address;
    }
    if (properties.poi_category?.length > 0) {
      placeName = properties.name;
    }

    if (address.includes(',') && (city || state || zip)) {
      address = address.split(',')[0].trim();
    }

    setScheduleForm(prev => ({
      ...prev,
      [type]: {
        place_name: placeName,
        address: address,
        city: city,
        state: state,
        zip: zip,
        lat: geometry.coordinates[1],
        lng: geometry.coordinates[0],
      },
    }));

    if (type === 'startLocation') {
      setStartLocationSearch('');
    } else if (type === 'endLocation') {
      setEndLocationSearch('');
    }
  };

  const getBaseSetting = () => {
    const setting = siteSetting();
    if (!setting || setting.lat == '0' || setting.lon == '0') {
      return null;
    }
    return {
      lat: parseFloat(setting.lat) / 1000000,
      lon: parseFloat(setting.lon) / 1000000,
    };
  };

  const getAddressString = (location) => {
    if (!location) return '-';

    let address = location.address || '';
    if (address.includes(',') && (location.city || location.state || location.zip)) {
      address = address.split(',')[0].trim();
    }

    const parts = [location.place_name, address, location.city, location.state, location.zip]
      .filter(str => notNull(str) && str.length > 0);

    return parts.length > 0 ? parts.join(', ') : '-';
  };

  const getScheduleFormDefaults = (vehicleIds) => {
    const setting = siteSetting();

    if (vehicleIds.length === 1) {
      const vehicle = vehicles.find(item => item.id === vehicleIds[0]);
      const formFromSchedule = scheduleToFormState(vehicle?.schedule, setting);
      if (formFromSchedule) {
        return formFromSchedule;
      }
    }

    const referenceVehicle = vehicleIds.length === 1 ? vehicles.find(item => item.id === vehicleIds[0]) : null;
    const location = vehicleAddressToScheduleLocation(getEffectiveVehicleAddress(referenceVehicle, setting));
    const { startTimeLocal, endTimeLocal } = getDefaultScheduleTimes(setting);

    return {
      startLocation: location,
      endLocation: location,
      startTime: startTimeLocal,
      endTime: endTimeLocal,
    };
  };

  const openScheduleDialog = (vehicleIds) => {
    setScheduleVehicleIds(vehicleIds);
    setScheduleForm(getScheduleFormDefaults(vehicleIds));
    setStartLocationSearch('');
    setEndLocationSearch('');
    setScheduleDialogOpen(true);
  };

  const validate = () => {
    if (!selected || selected.length === 0) {
      setAlertMessage('Please select at least one vehicle to continue');
      setShowAlert(true);
      return false;
    }

    // Check for vehicles without schedule information (start location and start time required)
    const unscheduledVehicles = vehicles.filter(
      vehicle => selected.includes(vehicle.id) && 
      (!vehicle.schedule?.startLocation || 
       (!vehicle.schedule.startLocation.place_name && !vehicle.schedule.startLocation.address) ||
       !vehicle.schedule?.startTime)
    );

    if (unscheduledVehicles.length > 0) {
      const unscheduledIds = unscheduledVehicles.map(vehicle => vehicle.id);
      setUnscheduledVehicleIds(unscheduledIds);
      setShowUnscheduledDialog(true);
      return false;
    }

    // Store selected vehicles with schedule information in routePlanData when validation passes
    const selectedVehicles = vehicles.filter(vehicle => selected.includes(vehicle.id));
    setRoutePlanData({
      ...routePlanData,
      vehicles: selectedVehicles,
    });

    return true;
  };

  const handleDeselectUnscheduledVehicles = () => {
    const newSelected = selected.filter(id => !unscheduledVehicleIds.includes(id));
    setSelected(newSelected);
    setShowUnscheduledDialog(false);
    setUnscheduledVehicleIds([]);
  };

  const handleCloseUnscheduledDialog = () => {
    setShowUnscheduledDialog(false);
    setUnscheduledVehicleIds([]);
  };

  useImperativeHandle(ref, () => ({
    validate,
  }));

  const handleSchedule = (vehicleId) => {
    openScheduleDialog([vehicleId]);
  };

  const handleBulkSchedule = () => {
    openScheduleDialog([...selected]);
  };

  const handleScheduleDialogClose = () => {
    setScheduleDialogOpen(false);
    setScheduleVehicleIds([]);
    setScheduleForm({
      startLocation: {
        place_name: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        lat: null,
        lng: null,
      },
      endLocation: {
        place_name: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        lat: null,
        lng: null,
      },
      startTime: null,
      endTime: null,
    });
    setStartLocationSearch('');
    setEndLocationSearch('');
  };

  const handleScheduleDialogSave = () => {
    // Validation: End time should be later than start time
    if (scheduleForm.startTime && scheduleForm.endTime) {
      if (moment(scheduleForm.endTime).isSameOrBefore(scheduleForm.startTime)) {
        setAlertMessage('End time must be later than start time');
        setShowAlert(true);
        return;
      }
    }

    // Convert times to UTC format
    let startTimeUTC = null;
    let endTimeUTC = null;

    if (scheduleForm.startTime) {
      const setting = siteSetting();
      if (setting && setting.timezone) {
        startTimeUTC = moment
          .tz(moment(scheduleForm.startTime).format('YYYY-MM-DD HH:mm:ss'), setting.timezone)
          .utc()
          .format('YYYY-MM-DD HH:mm:ss');
      } else {
        startTimeUTC = moment(scheduleForm.startTime).utc().format('YYYY-MM-DD HH:mm:ss');
      }
    }

    if (scheduleForm.endTime) {
      const setting = siteSetting();
      if (setting && setting.timezone) {
        endTimeUTC = moment
          .tz(moment(scheduleForm.endTime).format('YYYY-MM-DD HH:mm:ss'), setting.timezone)
          .utc()
          .format('YYYY-MM-DD HH:mm:ss');
      } else {
        endTimeUTC = moment(scheduleForm.endTime).utc().format('YYYY-MM-DD HH:mm:ss');
      }
    }

    // Update vehicles with schedule information
    const updatedVehicles = vehicles.map(vehicle => {
      if (scheduleVehicleIds.includes(vehicle.id)) {
        return {
          ...vehicle,
          schedule: {
            startLocation: scheduleForm.startLocation,
            endLocation: scheduleForm.endLocation,
            startTime: startTimeUTC,
            endTime: endTimeUTC,
          },
        };
      }
      return vehicle;
    });
    setVehicles(updatedVehicles);
    handleScheduleDialogClose();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const setting = siteSetting();
    if (!setting || !setting.timezone) {
      return '-';
    }
    // Parse UTC date string and convert to local timezone
    return moment.utc(dateString).tz(setting.timezone).format('MM/DD/YYYY hh:mm A');
  };

  const isSelected = (vehicleId) => selected.indexOf(vehicleId) !== -1;
  const numSelected = selected.length;
  const rowCount = vehicles.length;

  if (loading) {
    return <div>Loading vehicles...</div>;
  }

  return (
    <>
      <TableInfoContainer>
        <span>
          {numSelected} / {rowCount} vehicles selected
        </span>
      </TableInfoContainer>
      <StyledTableContainer>
        <Table aria-label="vehicle selection table" stickyHeader>
          <TableHead>
            <TableRow>
              <StyledTableHeadCell padding="checkbox" align="center">
                <Checkbox
                  indeterminate={numSelected > 0 && numSelected < rowCount}
                  checked={rowCount > 0 && numSelected === rowCount}
                  onChange={handleSelectAllClick}
                  inputProps={{ 'aria-label': 'select all vehicles' }}
                />
              </StyledTableHeadCell>
              <StyledTableHeadCell align="center">ID</StyledTableHeadCell>
              <StyledTableHeadCell align="center">Vehicle #</StyledTableHeadCell>
              <StyledTableHeadCell align="center">DMV License</StyledTableHeadCell>
              <StyledTableHeadCell align="center">VIN</StyledTableHeadCell>
              <StyledTableHeadCell align="center">Status</StyledTableHeadCell>
              <StyledTableHeadCell align="center">Start Location</StyledTableHeadCell>
              <StyledTableHeadCell align="center">End Location</StyledTableHeadCell>
              <StyledTableHeadCell align="center">Start Time</StyledTableHeadCell>
              <StyledTableHeadCell align="center">End Time</StyledTableHeadCell>
              <StyledTableHeadCell align="center">
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleBulkSchedule}
                  disabled={numSelected === 0}
                  style={{ minWidth: 'auto', padding: '4px' }}
                >
                  <EditTwoToneIcon fontSize="small" />
                </Button>
              </StyledTableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {vehicles.length === 0 ? (
              <TableRow>
                <StyledTableCell colSpan={11} align="center">
                  No vehicles found
                </StyledTableCell>
              </TableRow>
            ) : (
              vehicles.map((vehicle, index) => {
                const isItemSelected = isSelected(vehicle.id);
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow
                    key={`${vehicle.id}_${index}`}
                    hover
                    role="checkbox"
                    aria-checked={isItemSelected}
                    selected={isItemSelected}
                    onClick={(event) => handleClick(event, vehicle.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <StyledTableCell padding="checkbox" align="center" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isItemSelected}
                        onClick={(event) => handleClick(event, vehicle.id)}
                        inputProps={{ 'aria-labelledby': labelId }}
                      />
                    </StyledTableCell>
                    <StyledTableCell align="center" id={labelId}>
                      {vehicle.id}
                    </StyledTableCell>
                    <StyledTableCell align="center">{vehicle.vehicle_no || '-'}</StyledTableCell>
                    <StyledTableCell align="center">{vehicle.dmv_license || '-'}</StyledTableCell>
                    <StyledTableCell align="center">{vehicle.vin || '-'}</StyledTableCell>
                     <StyledTableCell align="center">{getStatusText(vehicle.status)}</StyledTableCell>
                     <StyledTableCellWrap align="center">{getAddressString(vehicle.schedule?.startLocation)}</StyledTableCellWrap>
                     <StyledTableCellWrap align="center">{getAddressString(vehicle.schedule?.endLocation)}</StyledTableCellWrap>
                     <StyledTableCell align="center">{formatDateTime(vehicle.schedule?.startTime)}</StyledTableCell>
                    <StyledTableCell align="center">{formatDateTime(vehicle.schedule?.endTime)}</StyledTableCell>
                     <StyledTableCell align="center" onClick={(e) => e.stopPropagation()}>
                       <Button
                         variant="outlined"
                         color="primary"
                         size="small"
                         onClick={(e) => { e.stopPropagation(); handleSchedule(vehicle.id); }}
                         style={{ minWidth: 'auto', padding: '4px' }}
                       >
                         <EditTwoToneIcon fontSize="small" />
                       </Button>
                     </StyledTableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>
      <Dialog
        open={scheduleDialogOpen}
        onClose={handleScheduleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Schedule {scheduleVehicleIds.length > 1 ? 'Vehicles' : 'Vehicle'}
        </DialogTitle>
        <StyledDialogContent>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '10px' }}>
            <div>
              <Suspense fallback={<div>Loading Mapbox...</div>}>
                <SearchBox
                  accessToken={process.env.MAPBOX_ACCESS_TOKEN}
                  placeholder="Start location name or address here"
                  onRetrieve={res => onGeocodeResult('startLocation', res)}
                  value={startLocationSearch}
                  onChange={d => setStartLocationSearch(d)}
                  options={{
                    language: 'en',
                    country: 'US,CA',
                    proximity: getBaseSetting(),
                  }}
                  theme={{
                    variables: {
                      colorBackground: 'white',
                      border: '1px solid #ccc',
                      borderRadius: '0',
                      boxShadow: 'none',
                    },
                  }}
                  popoverOptions={{
                    offset: 0,
                  }}
                />
              </Suspense>
              <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4, marginBottom: 7 }}>
                START: {getAddressString(scheduleForm.startLocation)}
              </div>
            </div>
            <div>
              <Suspense fallback={<div>Loading Mapbox...</div>}>
                <SearchBox
                  accessToken={process.env.MAPBOX_ACCESS_TOKEN}
                  placeholder="End location name or address here"
                  onRetrieve={res => onGeocodeResult('endLocation', res)}
                  value={endLocationSearch}
                  onChange={d => setEndLocationSearch(d)}
                  options={{
                    language: 'en',
                    country: 'US,CA',
                    proximity: getBaseSetting(),
                  }}
                  theme={{
                    variables: {
                      colorBackground: 'white',
                      border: '1px solid #ccc',
                      borderRadius: '0',
                      boxShadow: 'none',
                    },
                  }}
                  popoverOptions={{
                    offset: 0,
                  }}
                />
              </Suspense>
              <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4, marginBottom: 7 }}>
                END: {getAddressString(scheduleForm.endLocation)}
              </div>
            </div>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDateTimePicker
                inputVariant="outlined"
                format="MM/dd/yyyy hh:mm a"
                label="Start Time"
                value={scheduleForm.startTime}
                onChange={(date) => setScheduleForm({ ...scheduleForm, startTime: date })}
                fullWidth
              />
            </MuiPickersUtilsProvider>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDateTimePicker
                inputVariant="outlined"
                format="MM/dd/yyyy hh:mm a"
                label="End Time"
                value={scheduleForm.endTime}
                onChange={(date) => setScheduleForm({ ...scheduleForm, endTime: date })}
                fullWidth
              />
            </MuiPickersUtilsProvider>
          </div>
        </StyledDialogContent>
        <DialogActions>
          <Button onClick={handleScheduleDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleScheduleDialogSave} color="primary" variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showAlert}
        onClose={() => setShowAlert(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Error</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {alertMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAlert(false)} color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showUnscheduledDialog}
        onClose={handleCloseUnscheduledDialog}
        aria-labelledby="unscheduled-dialog-title"
        aria-describedby="unscheduled-dialog-description"
      >
        <DialogTitle id="unscheduled-dialog-title">Cannot Proceed with Unscheduled Vehicles</DialogTitle>
        <DialogContent>
          <DialogContentText id="unscheduled-dialog-description">
            Some vehicles don't have scheduled information (start location and start time). Do you want to deselect all the unscheduled vehicles?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUnscheduledDialog} color="primary">
            No
          </Button>
          <Button onClick={handleDeselectUnscheduledVehicles} color="primary" variant="contained" autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

VehicleStep.displayName = 'VehicleStep';

export default VehicleStep;
